import { Button, Table, TableBody, TableCell, TableRow } from "@mui/material";
import clsx from "clsx";
import React, { useState } from "react";
import {
  PipedaOfficerValues,
  EditPipedaPrivacyOfficerDialog,
} from "../../../dialog/EditPipedaPrivacyOfficerDialog";
import { SettingRow } from "./SettingRow";
import { OrganizationDto } from "../../../../../common/service/server/dto/OrganizationDto";
import { Button as AquiloButton } from "@truevault/aquilo";

type Props = {
  organization: OrganizationDto;
  dpoName: string | undefined;
  dpoEmail: string | undefined;
  dpoPhone: string | undefined;
  dpoAddress: string | undefined;
  pipedaOfficerName: string | undefined;
  pipedaOfficerEmail: string | undefined;
  pipedaOfficerPhone: string | undefined;
  pipedaOfficerAddress: string | undefined;
  handleFormSubmit: (values) => void;
  title?: string;
  helpText?: React.ReactNode;
  vertical?: boolean;
  showSkip?: boolean;
  useNewDesign?: boolean;
};

export const PrivacyCenterPipedaPrivacyOfficerRow: React.FC<Props> = ({
  organization,
  dpoName,
  dpoEmail,
  dpoPhone,
  dpoAddress,
  pipedaOfficerName,
  pipedaOfficerEmail,
  pipedaOfficerPhone,
  pipedaOfficerAddress,
  handleFormSubmit,
  title = "Canadian privacy laws require your organization to name a privacy officer in your online privacy policy.",
  helpText = "Your privacy officer is a person either inside your organization or external to it (such as a Data Privacy Officer (DPO) who is accountable for PIPEDA compliance. You can put the name of a member of your privacy team and your new privacy@yourdomain email address as the contact email.",
  vertical = false,
  useNewDesign = false,
}) => {
  const [dialogOpen, setDialogOpen] = useState(false);

  const hasCanadianPO = Boolean(pipedaOfficerName && pipedaOfficerEmail);

  return (
    <SettingRow title={title} helpText={helpText} vertical={vertical}>
      {hasCanadianPO && (
        <Table className="mt-0">
          <TableBody>
            <TableRow>
              <TableCell width="125px">Name or Title:</TableCell>
              <TableCell>{pipedaOfficerName}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell width="125px">Email:</TableCell>
              <TableCell>{pipedaOfficerEmail}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell width="125px">Telephone:</TableCell>
              <TableCell>{pipedaOfficerPhone}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell width="125px">Address:</TableCell>
              <TableCell>{pipedaOfficerAddress}</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      )}

      <div
        className={clsx(
          "align-items-center",
          { "flex-col": !vertical, "flex-row": vertical },
          "mt-md",
        )}
      >
        {useNewDesign ? (
          <AquiloButton color="tertiary" onClick={() => setDialogOpen(true)}>
            {hasCanadianPO ? "Edit Privacy Officer" : "Set Privacy Officer"}
          </AquiloButton>
        ) : (
          <Button variant="contained" onClick={() => setDialogOpen(true)}>
            {hasCanadianPO ? "Edit Privacy Officer" : "Set Privacy Officer"}
          </Button>
        )}
      </div>

      <EditPipedaPrivacyOfficerDialog
        organization={organization}
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onDone={(values: PipedaOfficerValues) =>
          handleFormSubmit({
            pipedaOfficerName: values.name,
            pipedaOfficerEmail: values.email,
            pipedaOfficerPhone: values.phone,
            pipedaOfficerAddress: values.address,
          })
        }
        values={{
          name: pipedaOfficerName,
          email: pipedaOfficerEmail,
          phone: pipedaOfficerPhone,
          address: pipedaOfficerAddress,
        }}
        dpo={
          dpoName && dpoEmail
            ? {
                name: dpoName,
                email: dpoEmail,
                phone: dpoPhone,
                address: dpoAddress,
              }
            : null
        }
      />
    </SettingRow>
  );
};
